import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Progress } from '../../components/ui/progress';
import BackupSettingsCard from '../../components/backup/BackupSettingsCard';
import {
  Archive,
  CloudArrowUp,
  CloudArrowDown,
  Trash,
  ArrowCounterClockwise,
  HardDrives,
  FolderOpen,
  File,
  Clock,
  CheckCircle,
  Warning,
  CircleNotch,
  Plus,
  MagnifyingGlass,
  CaretRight,
  DownloadSimple,
  Info,
  ShieldCheck,
  Calendar
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BackupRecoveryPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [backups, setBackups] = useState([]);
  const [deletedItems, setDeletedItems] = useState([]);
  const [folderStatus, setFolderStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('backups');
  
  // Backup creation dialog
  const [showCreateBackup, setShowCreateBackup] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [backupForm, setBackupForm] = useState({
    name: '',
    description: '',
    include_attachments: false,
    compress: true
  });
  
  // Restore dialog
  const [showRestore, setShowRestore] = useState(false);
  const [restoreBackup, setRestoreBackup] = useState(null);
  const [restoreForm, setRestoreForm] = useState({
    restore_mode: 'merge',
    collections: []
  });
  const [restoring, setRestoring] = useState(false);
  
  // Deleted items filter
  const [deletedFilter, setDeletedFilter] = useState({
    collection: 'all',
    days: 30
  });

  useEffect(() => {
    fetchData();
  }, [deletedFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // v2 backup list — sourced from /api/audit/backups (matches the
      // richer backup we now produce: full collection scan + GridFS).
      const [backupsRes, deletedRes, foldersRes] = await Promise.all([
        axios.get(`${API_URL}/audit/backups`),
        axios.get(`${API_URL}/company-backup/deleted-items`, {
          params: {
            collection: deletedFilter.collection !== 'all' ? deletedFilter.collection : undefined,
            days: deletedFilter.days
          }
        }).catch(() => ({ data: { deleted_items: [] } })),
        axios.get(`${API_URL}/company-backup/folder-status`).catch(() => ({ data: null }))
      ]);

      // Normalise v2 backup rows into the shape the existing table expects.
      const normalised = (backupsRes.data.backups || []).map(b => ({
        id: b.id,
        name: b.filename,
        description: `${b.record_count || 0} records${b.attachment_count ? ` · ${b.attachment_count} attachments` : ''}${b.version === '2.0' ? ' · v2' : ''}`,
        size_bytes: b.file_size || 0,
        created_at: b.created_at,
        created_by_name: b.created_by_name || b.created_by || '',
        file_exists: b.status === 'completed',
        compressed: true,
        status: b.status,
        record_count: b.record_count,
        attachment_count: b.attachment_count,
        version: b.version || '1.0',
        drive_uploaded: b.drive_uploaded,
        drive_view_link: b.drive_view_link,
        drive_uploaded_at: b.drive_uploaded_at,
      }));
      setBackups(normalised);
      setDeletedItems(deletedRes.data.deleted_items || []);
      setFolderStatus(foldersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load backup data');
    } finally {
      setLoading(false);
    }
  };

  const handleInitFolders = async () => {
    try {
      await axios.post(`${API_URL}/company-backup/init-folders`);
      toast.success('Folders initialized successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initialize folders');
    }
  };

  const handleCreateBackup = async () => {
    setCreatingBackup(true);
    try {
      const includeAtt = backupForm.include_attachments ? 'true' : 'false';
      await axios.post(
        `${API_URL}/audit/backups/create?include_users=true&include_attachments=${includeAtt}`,
        {},
      );
      toast.success(`Backup started ${backupForm.include_attachments ? '(with attachments)' : '(data only)'}`);
      setShowCreateBackup(false);
      setBackupForm({ name: '', description: '', include_attachments: false, compress: true });
      // Poll for completion progress
      setTimeout(fetchData, 1500);
      setTimeout(fetchData, 4000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create backup');
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleDownloadBackup = async (backup) => {
    try {
      const res = await axios.get(`${API_URL}/audit/backups/${backup.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Download failed');
    }
  };

  const handleRestoreBackup = async () => {
    if (!restoreBackup) return;

    setRestoring(true);
    try {
      await axios.post(
        `${API_URL}/audit/backups/${restoreBackup.id}/restore?restore_users=${restoreForm.restore_mode === 'replace' ? 'true' : 'false'}`,
      );
      toast.success('Backup restored successfully');
      setShowRestore(false);
      setRestoreBackup(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore backup');
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;

    try {
      await axios.delete(`${API_URL}/audit/backups/${backupId}`);
      toast.success('Backup deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete backup');
    }
  };

  const handleRestoreDeletedItem = async (item) => {
    try {
      await axios.post(`${API_URL}/company-backup/deleted-items/restore`, {
        item_id: item.id,
        collection: item.collection
      });
      toast.success('Item restored successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore item');
    }
  };

  const handlePermanentDelete = async (itemId) => {
    if (!confirm('This will permanently delete this item. Are you sure?')) return;
    
    try {
      await axios.delete(`${API_URL}/company-backup/deleted-items/${itemId}`);
      toast.success('Item permanently deleted');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete item');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-AE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCollectionLabel = (collection) => {
    const labels = {
      invoices: 'Invoices',
      quotations: 'Quotations',
      purchase_orders: 'Purchase Orders',
      grns: 'GRNs',
      contacts: 'Contacts',
      employees: 'Employees',
      departments: 'Departments',
      journal_entries: 'Journal Entries',
      accounts: 'Chart of Accounts',
      products: 'Products',
      payment_vouchers: 'Payment Vouchers',
      receipt_vouchers: 'Receipt Vouchers',
      bank_accounts: 'Bank Accounts'
    };
    return labels[collection] || collection;
  };

  if (loading && !folderStatus) {
    return (
      <div className="flex items-center justify-center h-96">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-recovery-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Backup & Recovery</h1>
          <p className="text-muted-foreground">
            Manage data backups and recover deleted items
          </p>
        </div>
        <Button onClick={() => setShowCreateBackup(true)} data-testid="create-backup-btn">
          <Plus size={16} className="mr-2" />
          Create Backup
        </Button>
      </div>

      {/* Storage Overview */}
      {folderStatus && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl p-6 text-white"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center">
                <HardDrives size={24} />
              </div>
              <div>
                <h3 className="font-semibold">Storage Overview</h3>
                <p className="text-sm text-white/70">Company data storage status</p>
              </div>
            </div>
            {!folderStatus.folders?.root?.exists && (
              <Button 
                variant="secondary" 
                size="sm"
                onClick={handleInitFolders}
              >
                Initialize Folders
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Archive size={18} />
                <span className="text-sm">Backups</span>
              </div>
              <p className="text-xl font-semibold">{backups.length}</p>
              <p className="text-xs text-white/60">
                {formatBytes(folderStatus.folders?.backups?.size_bytes || 0)}
              </p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Trash size={18} />
                <span className="text-sm">Deleted Items</span>
              </div>
              <p className="text-xl font-semibold">{deletedItems.length}</p>
              <p className="text-xs text-white/60">
                {formatBytes(folderStatus.folders?.deleted?.size_bytes || 0)}
              </p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <File size={18} />
                <span className="text-sm">Attachments</span>
              </div>
              <p className="text-xl font-semibold">
                {folderStatus.folders?.attachments?.file_count || 0}
              </p>
              <p className="text-xs text-white/60">
                {formatBytes(folderStatus.folders?.attachments?.size_bytes || 0)}
              </p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FolderOpen size={18} />
                <span className="text-sm">Total Storage</span>
              </div>
              <p className="text-xl font-semibold">{folderStatus.total_size_mb} MB</p>
              <p className="text-xs text-white/60">Across all folders</p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Auto-download to PC + Per-user Google Drive */}
      <BackupSettingsCard
        backups={backups.map(b => ({ ...b, filename: b.name, completed_at: b.created_at }))}
        onRefresh={fetchData}
        downloadBackup={handleDownloadBackup}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="backups" className="flex items-center gap-2">
            <Archive size={16} />
            Backups ({backups.length})
          </TabsTrigger>
          <TabsTrigger value="deleted" className="flex items-center gap-2">
            <Trash size={16} />
            Deleted Items ({deletedItems.length})
          </TabsTrigger>
        </TabsList>

        {/* Backups Tab */}
        <TabsContent value="backups" className="mt-4">
          <div className="bg-card border rounded-xl overflow-hidden">
            {backups.length === 0 ? (
              <div className="py-16 text-center">
                <Archive size={48} className="mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No backups yet</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Create your first backup to protect your data
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow key={backup.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{backup.name}</p>
                          {backup.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-xs">
                              {backup.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p>{formatBytes(backup.size_bytes)}</p>
                          <p className="text-xs text-muted-foreground">
                            {backup.compressed ? 'Compressed' : 'Uncompressed'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{formatDate(backup.created_at)}</p>
                          <p className="text-xs text-muted-foreground">
                            by {backup.created_by_name}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {backup.file_exists ? (
                          <Badge className="bg-emerald-100 text-emerald-700">
                            <CheckCircle size={12} className="mr-1" weight="fill" />
                            Available
                          </Badge>
                        ) : (
                          <Badge variant="destructive">
                            <Warning size={12} className="mr-1" />
                            Missing
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadBackup(backup)}
                            disabled={!backup.file_exists}
                            title="Download to my PC"
                            data-testid={`download-backup-${backup.id}`}
                          >
                            <DownloadSimple size={16} />
                          </Button>
                          {backup.drive_uploaded && backup.drive_view_link && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(backup.drive_view_link, '_blank')}
                              title="Open in Google Drive"
                              className="text-emerald-600 hover:text-emerald-700"
                              data-testid={`drive-link-${backup.id}`}
                            >
                              <CloudArrowUp size={16} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setRestoreBackup(backup);
                              setShowRestore(true);
                            }}
                            disabled={!backup.file_exists}
                            title="Restore from this backup"
                            data-testid={`restore-backup-${backup.id}`}
                          >
                            <ArrowCounterClockwise size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteBackup(backup.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-backup-${backup.id}`}
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Deleted Items Tab */}
        <TabsContent value="deleted" className="mt-4">
          <div className="bg-card border rounded-xl overflow-hidden">
            {/* Filters */}
            <div className="p-4 border-b flex items-center gap-4">
              <Select 
                value={deletedFilter.collection}
                onValueChange={(v) => setDeletedFilter(prev => ({ ...prev, collection: v }))}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="invoices">Invoices</SelectItem>
                  <SelectItem value="quotations">Quotations</SelectItem>
                  <SelectItem value="contacts">Contacts</SelectItem>
                  <SelectItem value="employees">Employees</SelectItem>
                  <SelectItem value="products">Products</SelectItem>
                  <SelectItem value="journal_entries">Journal Entries</SelectItem>
                </SelectContent>
              </Select>
              <Select 
                value={String(deletedFilter.days)}
                onValueChange={(v) => setDeletedFilter(prev => ({ ...prev, days: Number(v) }))}
              >
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Time period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {deletedItems.length === 0 ? (
              <div className="py-16 text-center">
                <Trash size={48} className="mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No deleted items</p>
                <p className="text-sm text-muted-foreground/70 mt-1">
                  Deleted items will appear here for recovery
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Deleted</TableHead>
                    <TableHead>Deleted By</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deletedItems.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        <p className="font-medium">
                          {item.original_data?.name || 
                           item.original_data?.invoice_number ||
                           item.original_data?.quotation_number ||
                           item.original_data?.reference ||
                           item.original_id}
                        </p>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getCollectionLabel(item.collection)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(item.deleted_at)}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.deleted_by_name || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRestoreDeletedItem(item)}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Restore this item"
                          >
                            <ArrowCounterClockwise size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handlePermanentDelete(item.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            title="Permanently delete"
                          >
                            <Trash size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Backup Dialog */}
      <Dialog open={showCreateBackup} onOpenChange={setShowCreateBackup}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudArrowUp size={20} />
              Create Backup
            </DialogTitle>
            <DialogDescription>
              Create a full backup of your company data
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Include Attachments</Label>
                <p className="text-xs text-muted-foreground">
                  Include every uploaded PDF, letterhead, signature, agreement file (GridFS). Turn OFF for a smaller, data-only ZIP.
                </p>
              </div>
              <Switch
                checked={backupForm.include_attachments}
                onCheckedChange={(v) => setBackupForm({ ...backupForm, include_attachments: v })}
                data-testid="brp-include-attachments"
              />
            </div>

            <div className="bg-muted rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Info size={16} className="text-muted-foreground mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p>This backup will include:</p>
                  <ul className="list-disc list-inside mt-1 text-xs">
                    <li>Every non-system MongoDB collection for your company (auto-discovered)</li>
                    <li>Users, contacts, transactions, inventory, tax settings</li>
                    <li>Job orders, agreements, vouchers, reports, audit logs</li>
                    <li>Uploaded files (GridFS) when &ldquo;Include Attachments&rdquo; is on</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateBackup(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateBackup} disabled={creatingBackup}>
              {creatingBackup ? (
                <>
                  <CircleNotch size={16} className="animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <CloudArrowUp size={16} className="mr-2" />
                  Create Backup
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Restore Dialog */}
      <Dialog open={showRestore} onOpenChange={setShowRestore}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CloudArrowDown size={20} />
              Restore Backup
            </DialogTitle>
            <DialogDescription>
              Restore data from: {restoreBackup?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Restore Mode</Label>
              <Select
                value={restoreForm.restore_mode}
                onValueChange={(v) => setRestoreForm({ ...restoreForm, restore_mode: v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">
                    Merge - Add/update without deleting existing data
                  </SelectItem>
                  <SelectItem value="replace">
                    Replace - Remove existing data before restore
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Warning size={16} className="text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Caution</p>
                  <p className="text-xs mt-1">
                    {restoreForm.restore_mode === 'replace' 
                      ? 'Replace mode will DELETE all existing data before restoring. This cannot be undone.'
                      : 'Merge mode will add new items and update existing ones based on ID match.'}
                  </p>
                </div>
              </div>
            </div>

            {restoreBackup?.collection_counts && (
              <div className="bg-muted rounded-lg p-3">
                <p className="text-sm font-medium mb-2">Backup Contents:</p>
                <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                  {Object.entries(restoreBackup.collection_counts)
                    .filter(([_, count]) => count > 0)
                    .map(([collection, count]) => (
                      <div key={collection} className="flex justify-between">
                        <span>{getCollectionLabel(collection)}</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRestore(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRestoreBackup} 
              disabled={restoring}
              variant={restoreForm.restore_mode === 'replace' ? 'destructive' : 'default'}
            >
              {restoring ? (
                <>
                  <CircleNotch size={16} className="animate-spin mr-2" />
                  Restoring...
                </>
              ) : (
                <>
                  <CloudArrowDown size={16} className="mr-2" />
                  Restore Backup
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
